import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { AccountImapService } from '../../core/services/account-imap/account-imap.service';
import { AlertService } from '../../core/services/alert/alert.service';
import { AccountImapConfig } from '../../core/interfaces/communication.interface';

@Component({
  selector: 'app-account-imap',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, RouterLink],
  templateUrl: './account-imap.component.html',
  styleUrl: './account-imap.component.css',
})
export class AccountImapComponent implements OnInit {
  form!: FormGroup;
  config: AccountImapConfig | null = null;
  isLoading = true;
  isSaving = false;
  isTesting = false;
  isDeleting = false;
  isNew = true;
  testResult: 'success' | 'error' | null = null;

  encryptionOptions = [
    { value: 'ssl', label: 'SSL' },
    { value: 'tls', label: 'TLS' },
    { value: 'starttls', label: 'STARTTLS' },
    { value: null, label: 'settings.account_imap.no_encryption' },
  ];

  constructor(
    private fb: FormBuilder,
    private accountImapService: AccountImapService,
    private alertService: AlertService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadConfig();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      host: ['', [Validators.required, Validators.maxLength(255)]],
      port: [993, [Validators.required, Validators.min(1), Validators.max(65535)]],
      encryption: ['ssl'],
      username: ['', [Validators.required, Validators.maxLength(255)]],
      password: ['', [Validators.maxLength(1024)]],
      validate_cert: [true],
    });
  }

  loadConfig(): void {
    this.isLoading = true;
    this.accountImapService.getConfig().subscribe({
      next: (res) => {
        this.config = res.data;
        this.isNew = false;
        this.form.patchValue({
          host: this.config!.host,
          port: this.config!.port,
          encryption: this.config!.encryption,
          username: this.config!.username,
          validate_cert: this.config!.validate_cert,
          password: '',
        });
        // Password not required on update
        this.form.get('password')?.clearValidators();
        this.form.get('password')?.updateValueAndValidity();
        this.isLoading = false;
      },
      error: (err) => {
        if (err.status === 404) {
          this.isNew = true;
          // Password required on create
          this.form.get('password')?.setValidators([Validators.required, Validators.maxLength(1024)]);
          this.form.get('password')?.updateValueAndValidity();
        }
        this.isLoading = false;
      },
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving = true;
    const body = this.buildBody();
    const request = this.isNew
      ? this.accountImapService.createConfig(body)
      : this.accountImapService.updateConfig(body);

    request.subscribe({
      next: (res) => {
        this.isSaving = false;
        this.config = res.data;
        this.isNew = false;
        this.form.get('password')?.clearValidators();
        this.form.get('password')?.updateValueAndValidity();
        this.form.patchValue({ password: '' });
        const msgKey = this.isNew
          ? 'swal_messages.account_imaps.account_imap_created'
          : 'swal_messages.account_imaps.account_imap_updated';
        this.alertService.success(msgKey);
      },
      error: (err) => {
        this.isSaving = false;
        if (err.status === 409) {
          this.alertService.error('swal_messages.account_imaps.account_imap_already_exists');
          this.loadConfig();
        } else if (err.status === 422) {
          this.alertService.error(err.error?.errors || 'swal_messages.error_occurred');
        } else {
          this.alertService.error('swal_messages.error_occurred');
        }
      },
    });
  }

  testConnection(): void {
    this.isTesting = true;
    this.testResult = null;
    const body = this.buildBody();
    this.accountImapService.testConnection(body).subscribe({
      next: () => {
        this.isTesting = false;
        this.testResult = 'success';
      },
      error: (err) => {
        this.isTesting = false;
        this.testResult = 'error';
        if (err.status === 502) {
          this.alertService.error('swal_messages.account_imaps.imap_connection_failed');
        } else {
          this.alertService.error('swal_messages.error_occurred');
        }
      },
    });
  }

  deleteConfig(): void {
    this.alertService.confirmation(
      'settings.account_imap.confirm_delete',
      'swal_messages.confirm'
    ).then((result: any) => {
      if (result.isConfirmed) {
        this.isDeleting = true;
        this.accountImapService.deleteConfig().subscribe({
          next: () => {
            this.isDeleting = false;
            this.config = null;
            this.isNew = true;
            this.form.reset({ host: '', port: 993, encryption: 'ssl', username: '', password: '', validate_cert: true });
            this.form.get('password')?.setValidators([Validators.required, Validators.maxLength(1024)]);
            this.form.get('password')?.updateValueAndValidity();
            this.alertService.success('swal_messages.account_imaps.account_imap_deleted');
          },
          error: () => {
            this.isDeleting = false;
            this.alertService.error('swal_messages.error_occurred');
          },
        });
      }
    });
  }

  private buildBody(): any {
    const val = this.form.value;
    const body: any = {
      host: val.host,
      port: val.port,
      encryption: val.encryption || null,
      username: val.username,
      validate_cert: val.validate_cert,
    };
    if (val.password) {
      body.password = val.password;
    }
    return body;
  }

  isFieldInvalid(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && control.touched);
  }
}
